/**
 * Queries e mutations do editor de `platform_settings` no Superadmin.
 *
 * Diferenças do `usePlatformSettings()` em `features/proposals/`:
 *   - Aquele é otimizado pro consumer **público** da proposta (shape
 *     agrupado + cache longo de 10min).
 *   - Este é raw — retorna `Record<key, string>` com TODOS os campos
 *     listados em `PLATFORM_SETTING_FIELDS` + staleTime zero pra o
 *     form sempre carregar o que o banco tem.
 *
 * O UPDATE usa `.upsert()` em vez de UPDATE puro pra permitir que um
 * setting que ainda não existia no banco (ex: ambiente novo sem o seed
 * completo) seja CRIADO ao ser salvo pela primeira vez. RLS
 * `platform_settings_all_superadmin` cobre INSERT+UPDATE com o mesmo guard.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';
import { PLATFORM_SETTINGS_KEY } from '../proposals/platform-settings.js';

import { PLATFORM_SETTING_FIELDS, type PlatformSettingsFormInput } from './schema.js';

export const PLATFORM_SETTINGS_ADMIN_KEY = ['platform-settings-admin'] as const;

interface RawSettingRow {
  key: string;
  value: unknown;
}

/** Busca os valores brutos dos campos listados em `PLATFORM_SETTING_FIELDS`.
 * Retorna `Record<key, string>` — converte JSON value pra string crua
 * (`JSON.stringify` quando não-string) pra o form mostrar algo editável
 * mesmo que o valor tenha sido gravado mal no passado. */
export function usePlatformSettingsAdmin() {
  return useQuery<PlatformSettingsFormInput, Error>({
    queryKey: PLATFORM_SETTINGS_ADMIN_KEY,
    staleTime: 0,
    queryFn: async () => {
      const keys = PLATFORM_SETTING_FIELDS.map((f) => f.key);
      const { data, error } = await supabase
        .from('platform_settings')
        .select('key, value')
        .in('key', keys);
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as RawSettingRow[];
      const byKey = new Map(rows.map((r) => [r.key, r.value]));

      const out: PlatformSettingsFormInput = {};
      for (const field of PLATFORM_SETTING_FIELDS) {
        const raw = byKey.get(field.key);
        if (typeof raw === 'string') {
          out[field.key] = raw;
        } else if (raw === null || raw === undefined) {
          out[field.key] = '';
        } else {
          // Shape antigo/malformado — preserva como string serializada
          // pro admin ver o que tá e consertar manualmente.
          out[field.key] = JSON.stringify(raw);
        }
      }
      return out;
    },
  });
}

/** Salva (upsert) um conjunto de platform_settings. Recebe os valores
 * finais; calcula diff implicitamente — o Postgres executa UPSERT, o que
 * gera no-op quando o valor não mudou (impacto mínimo + audit trail
 * pelo `updated_at`/`updated_by` permanece coerente).
 *
 * Grava `value` como string JSON (ex: `"KM Gestão"`) porque a coluna é
 * `jsonb` e strings JSON são a representação canônica de valor textual. */
export function useSavePlatformSettings(): UseMutationResult<
  void,
  Error,
  PlatformSettingsFormInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values) => {
      // Prepara as rows com valores trimados. Só campos definidos em
      // PLATFORM_SETTING_FIELDS entram — ignora chaves extras que
      // possam vir por acidente.
      const rows = PLATFORM_SETTING_FIELDS.map((field) => ({
        key: field.key,
        value: (values[field.key] ?? '').trim(),
      }));

      const { error } = await supabase.from('platform_settings').upsert(rows, {
        onConflict: 'key',
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      // Invalida tanto o cache do admin (próxima edição vê os valores
      // atualizados) quanto do consumer público (propostas renderizam
      // com os novos valores imediatamente).
      void qc.invalidateQueries({ queryKey: PLATFORM_SETTINGS_ADMIN_KEY });
      void qc.invalidateQueries({ queryKey: PLATFORM_SETTINGS_KEY });
    },
  });
}
